(() => {
  if (document.documentElement.dataset.noaStorefrontLoaded) return;
  document.documentElement.dataset.noaStorefrontLoaded = "true";

  const root = window.Shopify?.routes?.root || "/";

  const cartTargets = () => document.querySelectorAll(
    "[data-cart-count], .cart-bubble__text-count, .nb-cart-count"
  );

  const ensureCartCount = () => {
    const cartLink = document.querySelector(
      '.header a[href*="/cart"], .header-actions a[href*="/cart"], a.header-actions__action[href*="/cart"]'
    );
    if (!cartLink || cartLink.querySelector(".nb-cart-count")) return;
    cartLink.classList.add("nb-cart-link");
    const count = document.createElement("span");
    count.className = "nb-cart-count";
    count.setAttribute("aria-label", "פריטים בעגלה");
    count.textContent = "0";
    cartLink.append(count);
  };

  const paintCount = (count) => {
    ensureCartCount();
    cartTargets().forEach((target) => {
      target.textContent = String(count);
      target.toggleAttribute("hidden", count === 0);
    });
  };

  const refreshCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("cart-refresh");
    const cart = await response.json();
    paintCount(cart.item_count);
    return cart;
  };

  const notifyTheme = (cart, sections = {}) => {
    document.dispatchEvent(new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: "noa-product-grid",
        data: {
          source: "noa-product-grid",
          itemCount: cart.item_count,
          sections,
        },
      },
    }));
  };

  const bindCartForms = () => {
    document.querySelectorAll("[data-nb-cart-form]").forEach((form) => {
      if (form.dataset.nbBound) return;
      form.dataset.nbBound = "true";
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const button = form.querySelector("button[type='submit']");
        const label = button?.querySelector("span");
        const status = form.querySelector("[data-nb-cart-status]");
        if (!button || button.disabled) return;

        button.disabled = true;
        button.classList.add("is-loading");
        if (label) label.textContent = "מוסיף...";
        if (status) status.textContent = "";

        try {
          const payload = new FormData(form);
          const sectionIds = Array.from(document.querySelectorAll("cart-items-component"))
            .map((item) => item.dataset.sectionId)
            .filter(Boolean);
          if (sectionIds.length) {
            payload.append("sections", [...new Set(sectionIds)].join(","));
            payload.append("sections_url", window.location.pathname);
          }

          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json" },
            body: payload,
          });
          if (!response.ok) throw new Error("cart-add");
          const added = await response.json();
          const cart = await refreshCart();
          notifyTheme(cart, added.sections || {});
          if (label) label.textContent = "נוסף לעגלה";
          if (status) status.textContent = `יש ${cart.item_count} פריטים בעגלה`;
          window.setTimeout(() => {
            if (label) label.textContent = button.dataset.defaultLabel || "הוספה לעגלה";
            if (status) status.textContent = "";
          }, 1800);
        } catch (_) {
          if (label) label.textContent = button.dataset.defaultLabel || "הוספה לעגלה";
          if (status) status.textContent = "לא הצלחנו להוסיף. נסו שוב.";
        } finally {
          button.disabled = false;
          button.classList.remove("is-loading");
        }
      });
    });
  };

  const bindCarousels = () => {
    document.querySelectorAll("[data-nb-carousel]").forEach((carousel) => {
      if (carousel.dataset.nbBound) return;
      carousel.dataset.nbBound = "true";
      const track = carousel.querySelector("[data-nb-carousel-track]");
      if (!track) return;
      const move = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
      carousel.querySelector("[data-nb-carousel-prev]")?.addEventListener("click", () => move(-1));
      carousel.querySelector("[data-nb-carousel-next]")?.addEventListener("click", () => move(1));

      const originals = Array.from(track.children);
      if (originals.length < 2 || matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const clones = originals.map((item) => {
        const clone = item.cloneNode(true);
        clone.removeAttribute("id");
        clone.removeAttribute("data-shopify-editor-block");
        clone.setAttribute("aria-hidden", "true");
        track.appendChild(clone);
        return clone;
      });

      track.dataset.nbAutoplay = "true";
      let paused = false;
      let lastFrame = performance.now();
      let loopWidth = 0;
      const measure = () => {
        loopWidth = clones[0].offsetLeft - originals[0].offsetLeft;
      };
      measure();

      const pause = () => { paused = true; };
      const resume = () => {
        paused = false;
        lastFrame = performance.now();
      };
      carousel.addEventListener("pointerenter", pause);
      carousel.addEventListener("pointerleave", resume);
      carousel.addEventListener("pointerdown", pause);
      carousel.addEventListener("pointerup", resume);
      carousel.addEventListener("pointercancel", resume);
      carousel.addEventListener("focusin", pause);
      carousel.addEventListener("focusout", (event) => {
        if (!carousel.contains(event.relatedTarget)) resume();
      });
      addEventListener("resize", measure, { passive: true });

      const tick = (now) => {
        if (!carousel.isConnected) return;
        if (!paused && !document.hidden && loopWidth > 0) {
          const elapsed = Math.min(now - lastFrame, 64);
          track.scrollLeft += elapsed * 0.035;
          if (track.scrollLeft >= loopWidth) track.scrollLeft -= loopWidth;
        }
        lastFrame = now;
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  };

  const bindReveals = () => {
    const items = document.querySelectorAll(".nb-reveal, .nb-reveal-stagger > *");
    if (!items.length || matchMedia("(prefers-reduced-motion: reduce)").matches) {
      items.forEach((item) => item.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px" });
    items.forEach((item, index) => {
      item.style.setProperty("--nb-delay", `${Math.min(index % 6, 5) * 70}ms`);
      observer.observe(item);
    });
  };

  const translateFacetLabels = (scope = document) => {
    const labels = { Price: "מחיר", Availability: "מלאי" };
    scope.querySelectorAll?.(".facets__label").forEach((label) => {
      const translated = labels[label.textContent.trim()];
      if (translated) label.textContent = translated;
    });
  };

  const watchFacetLabels = () => {
    translateFacetLabels();
    const facets = document.querySelector("facets-form, .facets, #FacetFiltersForm");
    if (!facets || facets.dataset.noaTranslationObserver) return;
    facets.dataset.noaTranslationObserver = "true";
    new MutationObserver(() => translateFacetLabels(facets)).observe(facets, {
      childList: true,
      subtree: true,
    });
  };

  const quizPackPrices = [199, 299, 450];
  const formatQuizPrice = (value) => value.toLocaleString("he-IL", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  const updateQuizPrices = () => {
    document.querySelectorAll(".nq-md").forEach((modal) => {
      const rows = [...modal.querySelectorAll(".nq-q")].slice(0, 3);
      rows.forEach((row, index) => {
        const price = quizPackPrices[index];
        const perBottle = price / (index + 1);
        const saving = quizPackPrices[0] * (index + 1) - price;
        const total = row.querySelector(".nq-qp b");
        const per = row.querySelector(".nq-qp i");
        if (total) total.textContent = `₪${formatQuizPrice(price)}`;
        if (per) per.textContent = `₪${formatQuizPrice(perBottle)} לבקבוק`;
        row.querySelectorAll(".nq-sv, .nq-qs").forEach((item) => {
          const next = item.textContent.replace(/חיסכון ₪[\d,.]+/, `חיסכון ₪${formatQuizPrice(saving)}`);
          if (next !== item.textContent) item.textContent = next;
        });
      });
      const addon = modal.querySelector(".nq-adp");
      if (addon && addon.textContent !== "+₪199") addon.textContent = "+₪199";
      const selected = rows.findIndex((row) => row.classList.contains("nq-sel"));
      const cta = modal.querySelector(".nq-cta");
      if (cta && selected >= 0) {
        const total = quizPackPrices[selected] + (modal.querySelector(".nq-ad.on") ? 199 : 0);
        const next = cta.textContent.replace(/₪[\d,.]+/, `₪${formatQuizPrice(total)}`);
        if (next !== cta.textContent) cta.textContent = next;
      }
    });
  };

  const watchQuizPrices = () => {
    updateQuizPrices();
    if (document.documentElement.dataset.noaQuizPriceObserver) return;
    document.documentElement.dataset.noaQuizPriceObserver = "true";
    new MutationObserver(updateQuizPrices).observe(document.body, { childList: true, subtree: true });
    document.addEventListener("click", () => setTimeout(updateQuizPrices), true);
  };

  const replaceFreeShippingCopy = (scope = document.body) => {
    if (!scope) return;
    const replaceNode = (node) => {
      const current = node.nodeValue || "";
      if (!current.includes("המשלוח יחושב בקופה")) return;
      node.nodeValue = current.replaceAll("המשלוח יחושב בקופה", "המשלוח חינם");
    };
    if (scope.nodeType === Node.TEXT_NODE) {
      replaceNode(scope);
      return;
    }
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) replaceNode(node);
  };

  const watchFreeShippingCopy = () => {
    replaceFreeShippingCopy();
    if (document.documentElement.dataset.noaFreeShippingObserver) return;
    document.documentElement.dataset.noaFreeShippingObserver = "true";
    new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === "characterData") replaceFreeShippingCopy(record.target);
        record.addedNodes?.forEach((node) => replaceFreeShippingCopy(node));
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  const formatCartMoney = (amount) => {
    const currency = window.Shopify?.currency?.active || "ILS";
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format((Number(amount) || 0) / 100);
  };

  const cartNode = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const addCartDealLabels = (component) => {
    component.querySelectorAll(".cart-items__table-row").forEach((row) => {
      if (row.querySelector(".noa-cart__deal")) return;
      const compare = row.querySelector(".cart-items__unit-price-wrapper .compare-at-price");
      if (!compare) return;
      const values = Array.from(row.querySelectorAll(".cart-items__unit-price-wrapper span"))
        .filter((node) => !node.classList.contains("visually-hidden"))
        .map((node) => Number((node.textContent.match(/[\d,.]+/) || [0])[0].replaceAll(",", "")))
        .filter((value) => value > 0);
      const original = Number((compare.textContent.match(/[\d,.]+/) || [0])[0].replaceAll(",", ""));
      const current = values[0] || 0;
      if (!original || !current || original <= current) return;
      if (!/2 בקבוקים/.test(row.textContent)) return;
      const dealText = "הבקבוק השני ב־50% הנחה";
      const badge = cartNode("span", "noa-cart__deal", dealText);
      const target = row.querySelector(".cart-items__variants-wrapper") || row.querySelector(".cart-items__details");
      target?.insertAdjacentElement("afterend", badge);
    });
  };

  const noaBundleVariants = {
    2: { id: 71264393232637, price: 29900, compareAt: 39800 },
    3: { id: 71264393265405, price: 45000, compareAt: 59700 },
  };

  const noaFormulaCatalog = [
    { handle: "anxirelief", name: "AnxiRelief™", title: "לטיפול בחרדות, מחשבות וסטרס", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/AnxiRelief.png?v=1775855340" },
    { handle: "deeppeace-kids", name: "DeepPeace Kids™", title: "לטיפול בכעסים בילדים", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/DeepPeaceKids.png?v=1775855442" },
    { handle: "openheart-emotional-balance", name: "OpenHeart™", title: "לטיפול בהתפרצויות זעם מלוות באלימות", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/OpenHeart.png?v=1775858361" },
    { handle: "nightbrave-kids-calm-nights", name: "NightBrave Kids™", title: "לטיפול בגמילה מלישון עם ההורים", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/NightBraveKids.png?v=1775858534" },
    { handle: "panicfree-calm-confidence", name: "PanicFree™", title: "לטיפול בפאניקה וחרדה", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/PanicFree.png?v=1775858642" },
    { handle: "patienceflow-patience-calm", name: "PatienceFlow™", title: "לטיפול בסטרס וחוסר סבלנות", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/PatienceFlow.png?v=1775858669" },
    { handle: "riseabove-positive-energy", name: "RiseAbove™", title: "לטיפול בחוסר מוטיבציה ויאוש", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/RiseAbove.png?v=1775859034" },
    { handle: "selfglow-ביטחון-עצמי-וערך-עצמי", name: "SelfGlow™", title: "לטיפול בביקורת עצמית קשה", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/SelfGlow.png?v=1775859091" },
    { handle: "socialease-social-confidence", name: "SocialEase™", title: "לטיפול בחרדות חברתיות", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/SocialEase.png?v=1775859180" },
    { handle: "wholeself-שלמות-עצמית-ואיזון-פנימי", name: "WholeSelf™", title: "לטיפול בתשישות פיזית ורגשית", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/WholeSelf2.png?v=1775860399" },
    { handle: "brainboost-mental-clarity", name: "BrainBoost™", title: "לטיפול בקשיי למידה וריכוז", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/BrainBoost.png?v=1775860144" },
    { handle: "mindcalm-mental-clarity", name: "MindCalm™", title: "לטיפול במחשבות טורדניות וכפייתיות", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/MindCalm.png?v=1775860691" },
    { handle: "impulsecalm-kids-self-control", name: "ImpulseCalm Kids™", title: "לטיפול באימפולסיביות והתפרצויות בילדים", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/ImpulseCalmKids.png?v=1775860960" },
    { handle: "lifeanchor-emotional-stability", name: "LifeAnchor™", title: "לטיפול בחרדות המלוות בדיכאון קל", image: "//cdn.shopify.com/s/files/1/0813/0854/9373/files/LifeAnchor.png?v=1775860990" },
  ];

  const legacyBottleImages = new Map([
    ["anxirelief-label.png", "anxirelief"],
    ["deeppeace-label.png", "deeppeace-kids"],
    ["openheart-label.png", "openheart-emotional-balance"],
    ["nightbrave-label.png", "nightbrave-kids-calm-nights"],
    ["panicfree-label.png", "panicfree-calm-confidence"],
    ["patienceflow-label.png", "patienceflow-patience-calm"],
    ["riseabove-label.png", "riseabove-positive-energy"],
    ["selfglow-label.png", "selfglow-ביטחון-עצמי-וערך-עצמי"],
    ["socialease-label.png", "socialease-social-confidence"],
    ["wholeself-label.png", "wholeself-שלמות-עצמית-ואיזון-פנימי"],
    ["brainboost-label.png", "brainboost-mental-clarity"],
    ["mindcalm-label.png", "mindcalm-mental-clarity"],
    ["impulsecalm-label.png", "impulsecalm-kids-self-control"],
    ["lifeanchor-label.png", "lifeanchor-emotional-stability"],
  ].map(([filename, handle]) => [
    filename,
    noaFormulaCatalog.find((formula) => formula.handle === handle)?.image,
  ]));

  const restoreLegacyBottleUrl = (value) => {
    try {
      const source = new URL(value, window.location.href);
      const filename = decodeURIComponent(source.pathname.split("/").pop() || "").toLowerCase();
      const legacy = legacyBottleImages.get(filename);
      if (!legacy) return value;
      const target = new URL(legacy, window.location.href);
      const width = source.searchParams.get("width");
      if (width) target.searchParams.set("width", width);
      return target.toString();
    } catch (_) {
      return value;
    }
  };

  const restoreLegacyBottleValue = (value, isSrcset) => {
    if (!value?.includes("-label.png")) return value;
    if (!isSrcset) return restoreLegacyBottleUrl(value);
    return value.split(",").map((candidate) => {
      const [url, descriptor] = candidate.trim().split(/\s+/, 2);
      const restored = restoreLegacyBottleUrl(url);
      return descriptor ? `${restored} ${descriptor}` : restored;
    }).join(", ");
  };

  const legacyBottleSelector = [
    'img[src*="-label.png"]',
    'img[srcset*="-label.png"]',
    'source[srcset*="-label.png"]',
    '[data_max_resolution*="-label.png"]',
    '[data-featured-media-url*="-label.png"]',
    '[data-product-variant-media*="-label.png"]',
    'meta[content*="-label.png"]',
    'link[href*="-label.png"]',
  ].join(",");
  const legacyBottleAttributes = [
    "src",
    "srcset",
    "data_max_resolution",
    "data-featured-media-url",
    "data-product-variant-media",
    "content",
    "href",
  ];

  const restoreLegacyBottleImages = (scope = document) => {
    const nodes = [];
    if (scope.nodeType === Node.ELEMENT_NODE && scope.matches(legacyBottleSelector)) nodes.push(scope);
    scope.querySelectorAll?.(legacyBottleSelector).forEach((node) => nodes.push(node));
    nodes.forEach((node) => {
      legacyBottleAttributes.forEach((attribute) => {
        const current = node.getAttribute(attribute);
        const restored = restoreLegacyBottleValue(current, attribute === "srcset");
        if (restored && restored !== current) node.setAttribute(attribute, restored);
      });
    });
  };

  const watchLegacyBottleImages = () => {
    restoreLegacyBottleImages();
    if (document.documentElement.dataset.noaLegacyBottleObserver) return;
    document.documentElement.dataset.noaLegacyBottleObserver = "true";
    new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === "attributes") restoreLegacyBottleImages(record.target);
        record.addedNodes.forEach((node) => restoreLegacyBottleImages(node));
      });
    }).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: legacyBottleAttributes,
    });
  };

  const cartBottleCount = (cart) => cart.items.reduce((total, item) => {
    const bundleCount = Number(item.properties?._noa_bundle_count || 0);
    const variantTitle = (item.variant_title || "").trim();
    const bottlesPerItem = bundleCount || (/^3\s/.test(variantTitle) ? 3 : /^2\s/.test(variantTitle) ? 2 : 1);
    return total + bottlesPerItem * item.quantity;
  }, 0);

  const sectionIdsForCart = () => [...new Set(
    Array.from(document.querySelectorAll("cart-items-component[data-section-id]"))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean)
  )];

  const cartRequest = async (path, options) => {
    let response = await fetch(`${root}${path}`, options);
    if (response.status !== 429) return response;
    const retryAfter = Math.max(700, Number(response.headers.get("Retry-After") || 0) * 1000);
    await new Promise((resolve) => window.setTimeout(resolve, retryAfter));
    response = await fetch(`${root}${path}`, options);
    return response;
  };

  const noaBundleOfferDuration = ((12 * 60) + 32) * 60 * 1000;
  const noaBundleOfferStorageKey = "noaSecondBottleOfferEndsAt";
  const getNoaBundleOfferEnd = () => {
    const now = Date.now();
    try {
      const saved = Number(window.localStorage.getItem(noaBundleOfferStorageKey));
      if (Number.isFinite(saved) && saved > now) return saved;
      if (saved > 0 && saved <= now) return 0;
      const endsAt = now + noaBundleOfferDuration;
      window.localStorage.setItem(noaBundleOfferStorageKey, String(endsAt));
      return endsAt;
    } catch (_) {
      if (!window.__noaSecondBottleOfferEndsAt) {
        window.__noaSecondBottleOfferEndsAt = now + noaBundleOfferDuration;
      }
      return window.__noaSecondBottleOfferEndsAt;
    }
  };

  const addMixedBundleBuilder = (component, cart) => {
    const content = component.querySelector(".cart-drawer__content");
    const summary = component.querySelector(".cart-drawer__summary");
    if (!content || !summary || component.querySelector(".noa-cart__bundle-builder")) return;

    const sourceItem = cart.items.find((item) => (
      item.handle !== "noa-mixed-bundle" &&
      (item.variant_title || "").trim() === "בקבוק אחד" &&
      item.quantity > 0
    ));
    const sourceFormula = sourceItem
      ? noaFormulaCatalog.find((formula) => formula.handle === sourceItem.handle)
      : null;
    if (!sourceFormula) return;
    const offerEndsAt = getNoaBundleOfferEnd();
    if (!offerEndsAt) return;
    let activeSlot = 1;
    const selections = [sourceFormula, null];

    const builder = cartNode("section", "noa-cart__bundle-builder");
    builder.setAttribute("aria-labelledby", "noa-bundle-title");
    builder.innerHTML = `
      <div class="noa-cart__bundle-head">
        <h2 id="noa-bundle-title" class="noa-cart__bundle-title">בקבוק שני ב־50% הנחה לזמן מוגבל</h2>
        <p class="noa-cart__bundle-copy">בחרי בקבוק שני ושלמי <strong>299 ₪</strong> במקום <s>398 ₪</s>.</p>
        <div class="noa-cart__bundle-timer" role="timer" aria-label="הזמן שנותר להטבה">
          <span>ההטבה מסתיימת בעוד</span>
          <time data-noa-bundle-countdown datetime="PT12H32M">12:32:00</time>
        </div>
      </div>
      <div class="noa-cart__bundle-slots" aria-live="polite"></div>
      <button type="button" class="noa-cart__bundle-add" hidden>הוסיפי בקבוק שני ב־50% הנחה</button>
      <p class="noa-cart__bundle-status" role="status" aria-live="polite"></p>
    `;

    const picker = cartNode("div", "noa-cart__picker");
    picker.hidden = true;
    picker.setAttribute("role", "dialog");
    picker.setAttribute("aria-modal", "true");
    picker.setAttribute("aria-labelledby", "noa-picker-title");
    picker.innerHTML = `
      <button type="button" class="noa-cart__picker-backdrop" aria-label="סגירת בחירת הפורמולה"></button>
      <section class="noa-cart__picker-panel">
        <header class="noa-cart__picker-head">
          <div><span>בקבוקון <b data-noa-picker-slot>1</b></span><h3 id="noa-picker-title">בחרי פורמולה</h3></div>
          <button type="button" class="noa-cart__picker-close" aria-label="סגירה">×</button>
        </header>
        <label class="noa-cart__picker-search"><span class="visually-hidden">חיפוש פורמולה</span><input type="search" placeholder="חפשי לפי שם או צורך" autocomplete="off"></label>
        <div class="noa-cart__picker-grid"></div>
      </section>
    `;

    const slots = builder.querySelector(".noa-cart__bundle-slots");
    const addButton = builder.querySelector(".noa-cart__bundle-add");
    const status = builder.querySelector(".noa-cart__bundle-status");
    const search = picker.querySelector("input");
    const grid = picker.querySelector(".noa-cart__picker-grid");
    const pickerPanel = picker.querySelector(".noa-cart__picker-panel");

    const selectedComplete = () => selections.every(Boolean);
    const renderSlots = () => {
      slots.replaceChildren();
      const formula = selections[1];
      const slot = cartNode("button", `noa-cart__bundle-slot${formula ? " is-selected" : ""}`);
      slot.type = "button";
      slot.dataset.slot = "1";
      slot.setAttribute("aria-label", formula ? `שינוי הבקבוקון הנוסף: ${formula.name}` : "בחירת הבקבוק השני");
      const media = cartNode("span", "noa-cart__slot-media");
      if (formula) {
        const image = document.createElement("img");
        image.src = `${formula.image}&width=120`;
        image.alt = "";
        image.width = 52;
        image.height = 52;
        media.appendChild(image);
      } else {
        media.textContent = "+";
      }
      const copy = cartNode("span", "noa-cart__slot-copy");
      copy.append(
        cartNode("strong", "", formula?.name || "בחרי את הבקבוק השני"),
        cartNode("small", "", formula?.title || "לחצי כאן ובחרי מתוך כל הפורמולות")
      );
      slot.append(media, copy, cartNode("span", "noa-cart__slot-arrow", "‹"));
      slot.addEventListener("click", () => openPicker(1));
      slots.appendChild(slot);
      addButton.hidden = !formula;
      addButton.disabled = false;
      addButton.textContent = "הוסיפי בקבוק שני ב־50% הנחה";
    };

    const renderPicker = (filter = "") => {
      const query = filter.trim().toLocaleLowerCase("he");
      grid.replaceChildren();
      noaFormulaCatalog.filter((formula) => (
        !query || `${formula.name} ${formula.title}`.toLocaleLowerCase("he").includes(query)
      )).forEach((formula) => {
        const option = cartNode("button", "noa-cart__picker-option");
        option.type = "button";
        const image = document.createElement("img");
        image.src = `${formula.image}&width=120`;
        image.alt = "";
        image.width = 72;
        image.height = 72;
        const copy = cartNode("span", "");
        copy.append(cartNode("strong", "", formula.name), cartNode("small", "", formula.title));
        option.append(image, copy);
        option.addEventListener("click", () => {
          selections[activeSlot] = formula;
          closePicker();
          renderSlots();
          builder.querySelector(`[data-slot="${activeSlot}"]`)?.focus();
        });
        grid.appendChild(option);
      });
      if (!grid.children.length) grid.appendChild(cartNode("p", "noa-cart__picker-empty", "לא נמצאה פורמולה. נסי מילה אחרת."));
    };

    const openPicker = (slotIndex) => {
      activeSlot = slotIndex;
      picker.querySelector("[data-noa-picker-slot]").textContent = String(slotIndex + 1);
      search.value = "";
      renderPicker();
      picker.hidden = false;
      component.classList.add("noa-cart--picker-open");
      window.requestAnimationFrame(() => search.focus());
    };

    const closePicker = () => {
      picker.hidden = true;
      component.classList.remove("noa-cart--picker-open");
    };

    picker.querySelector(".noa-cart__picker-close").addEventListener("click", closePicker);
    picker.querySelector(".noa-cart__picker-backdrop").addEventListener("click", closePicker);
    picker.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePicker();
      if (event.key !== "Tab") return;
      const focusable = Array.from(pickerPanel.querySelectorAll("button:not([disabled]), input"));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    search.addEventListener("input", () => renderPicker(search.value));

    addButton.addEventListener("click", async () => {
      if (addButton.disabled || !selectedComplete()) return;
      const offer = noaBundleVariants[2];
      const chosen = selections.slice(0, 2);
      const properties = { _noa_bundle_count: "2", _noa_bundle_handles: chosen.map((item) => item.handle).join("|") };
      chosen.forEach((formula, index) => { properties[`בקבוקון ${index + 1}`] = `${formula.name} · ${formula.title}`; });
      addButton.disabled = true;
      addButton.textContent = "מוסיפים את המארז…";
      status.textContent = "";
      component.toggleAttribute("data-noa-cart-busy", true);
      let addedKey = "";
      try {
        const addResponse = await cartRequest("cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: offer.id, quantity: 1, properties }] }),
        });
        if (!addResponse.ok) throw new Error("bundle-add");
        const added = await addResponse.json();
        addedKey = added.items?.[0]?.key || "";
        let nextCart;
        if (sourceItem) {
          const changeResponse = await cartRequest("cart/change.js", {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({
              id: sourceItem.key,
              quantity: sourceItem.quantity - 1,
              sections: sectionIdsForCart().join(","),
              sections_url: window.location.pathname,
            }),
          });
          if (!changeResponse.ok) throw new Error("bundle-source-change");
          nextCart = await changeResponse.json();
        } else {
          nextCart = await refreshCart();
        }
        paintCount(cartBottleCount(nextCart));
        notifyTheme(nextCart, nextCart.sections || added.sections || {});
        window.dataLayer?.push({ event: "noa_mixed_bundle_added", bottle_count: 2, value: offer.price / 100 });
      } catch (_) {
        if (addedKey) {
          await cartRequest("cart/change.js", {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({ id: addedKey, quantity: 0 }),
          }).catch(() => {});
        }
        status.textContent = "לא הצלחנו לעדכן את הסל. שום פריט לא הוסר, אפשר לנסות שוב.";
        addButton.disabled = false;
        addButton.textContent = "נסי שוב";
      } finally {
        component.removeAttribute("data-noa-cart-busy");
      }
    });

    const wasAtTop = content.scrollTop < 24;
    summary.insertAdjacentElement("beforebegin", builder);
    if (wasAtTop) window.requestAnimationFrame(() => { content.scrollTop = 0; });
    component.appendChild(picker);
    renderSlots();

    const countdown = builder.querySelector("[data-noa-bundle-countdown]");
    let countdownTimer = 0;
    const updateCountdown = () => {
      if (!builder.isConnected) {
        window.clearInterval(countdownTimer);
        return;
      }
      const remaining = Math.max(0, offerEndsAt - Date.now());
      const totalSeconds = Math.ceil(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      countdown.textContent = [hours, minutes, seconds]
        .map((value) => String(value).padStart(2, "0"))
        .join(":");
      if (remaining > 0) return;
      window.clearInterval(countdownTimer);
      picker.remove();
      builder.remove();
    };
    updateCountdown();
    countdownTimer = window.setInterval(updateCountdown, 1000);
  };

  const addCartRecommendation = async (component, cart) => {
    const content = component.querySelector(".cart-drawer__content");
    const summary = component.querySelector(".cart-drawer__summary");
    if (!content || !summary || !cart.items?.length || content.querySelector(".noa-cart__recommendation")) return;

    const shell = cartNode("section", "noa-cart__recommendation noa-cart__recommendation--loading");
    shell.dataset.productId = String(cart.items[0].product_id || "");
    shell.setAttribute("aria-label", "המלצה משלימה");
    shell.appendChild(cartNode("h3", "noa-cart__recommendation-title", "אולי תרצי להוסיף"));
    shell.appendChild(cartNode("p", "noa-cart__recommendation-status", "טוענים המלצה שמתאימה לעגלה…"));
    summary.insertAdjacentElement("beforebegin", shell);

    try {
      const existingIds = new Set(cart.items.map((item) => String(item.product_id)));
      const productId = cart.items[0].product_id;
      const fetchProducts = async (intent) => {
        const url = `${root}recommendations/products.json?product_id=${productId}&limit=6&intent=${intent}`;
        const response = await fetch(url, { headers: { Accept: "application/json" } });
        if (!response.ok) return [];
        return (await response.json()).products || [];
      };
      let products = await fetchProducts("complementary");
      if (!products.length) products = await fetchProducts("related");
      const product = products.find((entry) => entry.available && !existingIds.has(String(entry.id)));
      const variant = product?.variants?.find((entry) => entry.available) || product?.variants?.[0];
      if (!product || !variant) {
        shell.remove();
        return;
      }

      shell.classList.remove("noa-cart__recommendation--loading");
      shell.querySelector(".noa-cart__recommendation-status")?.remove();
      const item = cartNode("div", "noa-cart__recommendation-item");
      const link = cartNode("a", "noa-cart__recommendation-media");
      link.href = product.url || `${root}products/${product.handle}`;
      const image = document.createElement("img");
      image.src = typeof product.featured_image === "string" ? product.featured_image : product.featured_image?.url || "";
      image.alt = product.featured_image?.alt || product.title;
      image.width = 96;
      image.height = 96;
      image.loading = "lazy";
      link.appendChild(image);
      const copy = cartNode("div", "noa-cart__recommendation-copy");
      const title = cartNode("a", "noa-cart__recommendation-name", product.title);
      title.href = link.href;
      copy.append(title, cartNode("span", "noa-cart__recommendation-price", formatCartMoney(variant.price)));
      const button = cartNode("button", "noa-cart__recommendation-add", "הוספה");
      button.type = "button";
      button.addEventListener("click", async () => {
        if (button.disabled) return;
        button.disabled = true;
        button.textContent = "מוסיפים…";
        try {
          const sectionIds = Array.from(document.querySelectorAll("cart-items-component[data-section-id]"))
            .map((node) => node.dataset.sectionId)
            .filter(Boolean);
          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({
              items: [{ id: variant.id, quantity: 1 }],
              sections: [...new Set(sectionIds)].join(","),
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) throw new Error("recommendation-add");
          const added = await response.json();
          const nextCart = await refreshCart();
          notifyTheme(nextCart, added.sections || {});
          button.textContent = "נוסף";
        } catch (_) {
          button.disabled = false;
          button.textContent = "נסי שוב";
        }
      });
      item.append(link, copy, button);
      shell.appendChild(item);
    } catch (_) {
      shell.remove();
    }
  };

  const enhanceCartDrawer = (cart) => {
    document.querySelectorAll("cart-drawer-component cart-items-component").forEach((component) => {
      const heading = component.querySelector(".cart-drawer__heading:not(.cart-drawer__heading--empty)");
      const header = component.querySelector(".cart-drawer__header");
      if (!heading || !header || !cart.item_count) return;

      const label = `עגלת הקניות שלך (${cartBottleCount(cart)})`;
      heading.setAttribute("aria-label", label);
      const textNode = Array.from(heading.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim());
      if (textNode) textNode.nodeValue = label;

      if (!component.querySelector(".noa-cart__shipping")) {
        const shipping = cartNode("section", "noa-cart__shipping");
        shipping.setAttribute("aria-label", "משלוח חינם");
        shipping.append(
          cartNode("strong", "noa-cart__shipping-title", "את זכאית למשלוח חינם"),
          cartNode("span", "noa-cart__shipping-bar")
        );
        header.insertAdjacentElement("afterend", shipping);
      }

      addCartDealLabels(component);

      component.querySelectorAll(".noa-cart__quiz-banner, .noa-cart__second-bottle, .noa-cart__recommendation").forEach((node) => node.remove());
      addMixedBundleBuilder(component, cart);

      const checkoutText = component.querySelector(".cart__checkout-button .button-text");
      if (checkoutText) checkoutText.textContent = "לתשלום מאובטח";
      const taxNote = component.querySelector(".cart-totals__tax-note small");
      if (taxNote) taxNote.textContent = "כולל מע״מ. המשלוח חינם.";

      const ctas = component.querySelector(".cart__ctas");
      if (ctas && !component.querySelector(".noa-cart__trust")) {
        const trust = cartNode("div", "noa-cart__trust");
        trust.append(
          cartNode("span", "noa-cart__payments", "VISA · Mastercard · Amex · Apple Pay · G Pay · bit"),
          cartNode("a", "noa-cart__returns", "החזרה עד 14 יום, בכפוף למדיניות")
        );
        trust.querySelector("a").href = `${root}policies/refund-policy`;
        ctas.insertAdjacentElement("afterend", trust);
      }

    });
  };

  let cartDrawerTimer;
  let cartDrawerRevision = 0;
  const queueCartDrawerEnhancement = () => {
    window.clearTimeout(cartDrawerTimer);
    const revision = ++cartDrawerRevision;
    cartDrawerTimer = window.setTimeout(() => {
      refreshCart().then((cart) => {
        if (revision === cartDrawerRevision) enhanceCartDrawer(cart);
      }).catch(() => {});
    }, 40);
  };

  const watchCartDrawer = () => {
    if (document.documentElement.dataset.noaCartDrawerObserver) return;
    document.documentElement.dataset.noaCartDrawerObserver = "true";
    new MutationObserver((records) => {
      if (records.some((record) => Array.from(record.addedNodes).some((node) =>
        node.nodeType === Node.ELEMENT_NODE && (
          node.matches?.("cart-items-component, .cart-drawer__content, .cart-items__table-row") ||
          node.querySelector?.("cart-items-component, .cart-drawer__content, .cart-items__table-row")
        )
      ))) queueCartDrawerEnhancement();
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener("cart:update", queueCartDrawerEnhancement);
    queueCartDrawerEnhancement();
  };

  const ensureBreadcrumbs = () => {
    const rootUrl = new URL(root, window.location.origin);
    const rootPath = rootUrl.pathname.replace(/\/+$/, "") || "/";
    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    if (currentPath === rootPath || document.querySelector(".noa-breadcrumbs")) return;

    const main = document.querySelector("main");
    if (!main) return;

    const relativePath = currentPath
      .slice(rootPath === "/" ? 0 : rootPath.length)
      .replace(/^\/+/, "");
    const parts = relativePath.split("/").filter(Boolean);
    const pageType = parts[0] || "";
    const absolute = (path) => new URL(path.replace(/^\/+/, ""), rootUrl).href;
    const titleFromDocument = document.title
      .replace(/\s+(?:[–—-]|\|)\s+NOA.*$/i, "")
      .trim();
    const visibleHeading = document.querySelector("main h1")?.textContent?.replace(/\s+/g, " ").trim();
    const currentTitle = titleFromDocument || visibleHeading || "NOA";
    const items = [{ name: "דף הבית", url: rootUrl.href }];

    if (pageType === "products" || (pageType === "collections" && parts[2] === "products")) {
      items.push({ name: "כל המוצרים", url: absolute("collections/all") });
      items.push({ name: currentTitle });
    } else if (pageType === "blogs" && parts.length > 2) {
      items.push({ name: "מגזין NOA", url: absolute(`blogs/${parts[1]}`) });
      items.push({ name: currentTitle });
    } else {
      const labels = {
        cart: "סל הקניות",
        search: "חיפוש",
        account: "החשבון שלי",
        policies: currentTitle,
        collections: currentTitle,
        pages: currentTitle,
        blogs: "מגזין NOA",
      };
      items.push({ name: labels[pageType] || currentTitle });
    }

    const style = document.createElement("style");
    style.id = "noa-breadcrumbs-style";
    style.textContent = `
      .noa-breadcrumbs{width:min(100%,1280px);margin:0 auto;padding:18px clamp(16px,4vw,48px) 0;box-sizing:border-box;color:rgb(var(--color-foreground-rgb)/.62);font:650 13px/1.5 var(--nb-font,inherit)}
      .noa-breadcrumbs__list{display:flex;align-items:center;gap:8px;margin:0;padding:0;overflow-x:auto;list-style:none;white-space:nowrap;scrollbar-width:none}
      .noa-breadcrumbs__list::-webkit-scrollbar{display:none}
      .noa-breadcrumbs__item{display:flex;align-items:center;min-width:0}
      .noa-breadcrumbs__item+.noa-breadcrumbs__item::before{content:"›";margin-inline-end:8px;color:rgb(var(--color-foreground-rgb)/.35);font-size:17px;line-height:1}
      .noa-breadcrumbs a{color:inherit;text-decoration:none;transition:color .18s ease}
      .noa-breadcrumbs a:hover{color:rgb(var(--color-foreground-rgb));text-decoration:underline;text-underline-offset:3px}
      .noa-breadcrumbs [aria-current="page"]{display:block;overflow:hidden;color:rgb(var(--color-foreground-rgb)/.82);text-overflow:ellipsis}
      @media(max-width:749px){.noa-breadcrumbs{padding-block-start:12px;font-size:12px}.noa-breadcrumbs__list{gap:6px}.noa-breadcrumbs__item+.noa-breadcrumbs__item::before{margin-inline-end:6px}}
    `;
    document.head.appendChild(style);

    const nav = document.createElement("nav");
    nav.className = "noa-breadcrumbs";
    nav.dir = "rtl";
    nav.setAttribute("aria-label", "פירורי לחם");
    const list = document.createElement("ol");
    list.className = "noa-breadcrumbs__list";
    items.forEach((item) => {
      const entry = document.createElement("li");
      entry.className = "noa-breadcrumbs__item";
      if (item.url) {
        const link = document.createElement("a");
        link.href = item.url;
        link.textContent = item.name;
        entry.appendChild(link);
      } else {
        const current = document.createElement("span");
        current.setAttribute("aria-current", "page");
        current.textContent = item.name;
        entry.appendChild(current);
      }
      list.appendChild(entry);
    });
    nav.appendChild(list);
    main.insertAdjacentElement("afterbegin", nav);

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.noaBreadcrumbsSchema = "true";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url || document.querySelector('link[rel="canonical"]')?.href || window.location.href,
      })),
    });
    document.head.appendChild(schema);
  };

  const init = () => {
    ensureCartCount();
    refreshCart().then(enhanceCartDrawer).catch(() => {});
    bindCartForms();
    bindCarousels();
    bindReveals();
    watchFacetLabels();
    watchQuizPrices();
    watchFreeShippingCopy();
    watchCartDrawer();
    ensureBreadcrumbs();
  };

  watchLegacyBottleImages();
  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init, { once: true })
    : init();
  document.addEventListener("shopify:section:load", init);
})();
